import { pad, type Address } from "viem";
import {
  OFT_DUST_GRANULARITY,
  OFT_HUB,
  OFT_SPOKES,
  type OftChainEntry,
} from "@config/oftBridgeConfig";

export type OftRoute = {
  sourceChainId: number;
  destinationChainId: number;
  /** Contract exposing `quoteSend` / `send` on the source chain. */
  sourceContract: Address;
  /** rsETH ERC-20 whose balance is bridgeable on the source chain. */
  tokenAddress: Address;
  /** LayerZero endpoint id of the destination chain. */
  dstEid: number;
  /** True only on the Ethereum leg, whose adapter pulls the ERC-20. */
  needsApproval: boolean;
  /** Approval spender. Equal to `sourceContract`; named separately for clarity at call sites. */
  spender: Address;
};

export type SendParam = {
  dstEid: number;
  to: `0x${string}`;
  amountLD: bigint;
  minAmountLD: bigint;
  extraOptions: `0x${string}`;
  composeMsg: `0x${string}`;
  oftCmd: `0x${string}`;
};

export const getOftChainEntry = (chainId: number): OftChainEntry | undefined =>
  OFT_HUB.chain.id === chainId
    ? OFT_HUB
    : OFT_SPOKES.find((spoke) => spoke.chain.id === chainId);

/** Floors to the OFT's shared-decimal granularity; anything finer is dust the contract drops. */
export const roundToDust = (amountWei: bigint): bigint =>
  (amountWei / OFT_DUST_GRANULARITY) * OFT_DUST_GRANULARITY;

/**
 * Resolves a single bridge leg.
 *
 * Peers are hub-and-spoke only, so exactly one side must be Ethereum. Returns
 * null for anything else — same-chain, L2 -> L2, or any chain outside the mesh.
 */
export const getOftRoute = (
  sourceChainId: number,
  destinationChainId: number
): OftRoute | null => {
  if (sourceChainId === destinationChainId) return null;

  const hubId = OFT_HUB.chain.id;
  if (sourceChainId !== hubId && destinationChainId !== hubId) return null;

  const source = getOftChainEntry(sourceChainId);
  const destination = getOftChainEntry(destinationChainId);
  if (!source || !destination) return null;

  return {
    sourceChainId,
    destinationChainId,
    sourceContract: source.oft,
    tokenAddress: source.token,
    dstEid: destination.eid,
    needsApproval: source.approvalRequired,
    spender: source.oft,
  };
};

export const buildSendParam = ({
  dstEid,
  recipient,
  amountLD,
}: {
  dstEid: number;
  recipient: Address;
  amountLD: bigint;
}): SendParam => ({
  dstEid,
  to: pad(recipient, { size: 32 }),
  amountLD,
  // OFT transfers are 1:1 once dust-rounded, so there is no slippage to allow for.
  minAmountLD: amountLD,
  // enforcedOptions (200k gas) are configured on every route, so no encoding is needed.
  extraOptions: "0x",
  composeMsg: "0x",
  oftCmd: "0x",
});

export type BridgeCtaState =
  | { kind: "connect" }
  | { kind: "switch"; chainName: string }
  | { kind: "enter-amount" }
  | { kind: "balance-loading" }
  | { kind: "over-balance" }
  | { kind: "below-dust" }
  | { kind: "quote-loading" }
  | { kind: "quote-error" }
  | { kind: "insufficient-fee"; symbol: string }
  | { kind: "ready" };

/**
 * Single source of truth for what the card's button says and whether it acts.
 *
 * Order matters: each branch assumes the ones above it were false, which is what
 * keeps the states mutually exclusive.
 *
 * `balance-loading` sits after `enter-amount` (a user who has typed nothing
 * doesn't need to wait on a balance fetch to be told to enter an amount) but
 * before `over-balance` and `insufficient-fee` — both would draw a false
 * conclusion from a `bigint | undefined` balance that hasn't resolved yet, or
 * that failed to fetch. It comes after `connect`/`switch` because the balance
 * queries are legitimately disabled/unfetched while disconnected or on the
 * wrong chain, and those states are more informative anyway.
 */
export const resolveBridgeCtaState = ({
  isConnected,
  connectedChainId,
  sourceChainId,
  sourceChainName,
  nativeSymbol,
  typedAmountLD,
  roundedAmountLD,
  balanceLD,
  nativeBalance,
  quoteFee,
  isQuoteLoading,
  isQuoteError,
}: {
  isConnected: boolean;
  connectedChainId?: number;
  sourceChainId: number;
  sourceChainName: string;
  nativeSymbol: string;
  /** What the user typed, before dust rounding. */
  typedAmountLD: bigint;
  /** After dust rounding — this is what actually gets sent. */
  roundedAmountLD: bigint;
  /** `undefined` while the balance query is loading or has errored. */
  balanceLD: bigint | undefined;
  /** `undefined` while the balance query is loading or has errored. */
  nativeBalance: bigint | undefined;
  quoteFee: bigint | null;
  isQuoteLoading: boolean;
  isQuoteError: boolean;
}): BridgeCtaState => {
  if (!isConnected) return { kind: "connect" };
  if (connectedChainId !== sourceChainId) {
    return { kind: "switch", chainName: sourceChainName };
  }
  if (typedAmountLD <= 0n) return { kind: "enter-amount" };
  if (balanceLD === undefined || nativeBalance === undefined) {
    return { kind: "balance-loading" };
  }
  if (typedAmountLD > balanceLD) return { kind: "over-balance" };
  // Caught after over-balance so a too-small amount reads as "too small", not "too much".
  if (roundedAmountLD <= 0n) return { kind: "below-dust" };
  if (isQuoteError) return { kind: "quote-error" };
  if (isQuoteLoading || quoteFee === null) return { kind: "quote-loading" };
  if (nativeBalance < quoteFee) {
    return { kind: "insufficient-fee", symbol: nativeSymbol };
  }
  return { kind: "ready" };
};
